import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import { Grid, Typography, Tooltip, IconButton } from '@material-ui/core';
import { useHistory } from 'react-router-dom';

import Loader from 'components/Loader';
import Button from 'components/Button';
import MTable from 'components/MTable';
import TableMobile from 'components/TableMobile';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faCopy, faPen } from '@fortawesome/free-solid-svg-icons';

import { loadBusinessUuid, loadTokens } from '../store/thunk';
import { loadRestrictions } from 'pages/EmployeesRegistration/store/thunk';

import useWindowDimensions from '../../../helpers/hooks/useWindowDimensions';

const headers = [
  { title: 'Nome', field: 'name', sortable: false },
  { title: 'Token', field: 'token', sortable: false },
  { title: 'Último uso', field: 'lastUsed', sortable: false },
  { title: 'Data de criação', field: 'createdAt', sortable: false },
  { title: 'Data de expiração', field: 'expiresAt', sortable: false }
];

export default function OpenAPI() {
  const dispatch = useDispatch();

  const history = useHistory();

  const { width } = useWindowDimensions();
  const isMobile = width < 960;

  const { userId } = useSelector(state => state.profile);
  const { isLoading, businessUuid, list } = useSelector(state => state.openApi);

  useEffect(() => {
    if(userId) {
      dispatch(loadBusinessUuid(userId));
      dispatch(loadRestrictions(userId));
    }
  }, [userId]);

  useEffect(() => {
    if(userId && businessUuid) {
      dispatch(loadTokens(userId, businessUuid));
    }
  }, [userId, businessUuid]);

  const getExpirationDate = (expiresAt, createdAt) => {
    if(moment(expiresAt).utc(true).isBefore(moment.now())) {
      return 'Expirado';
    }

    if(expiresAt == null || expiresAt == '0001-01-01 00:00:00') {
      return 'Sem expiração';
    }

    if(expiresAt == createdAt) {
      return 'Sem expiração';
    }

    return moment(expiresAt).utc(true).format('DD/MM/YYYY');
  }

  return(
    <Loader isLoading={isLoading}>
      <Grid container direction='row' justify='flex-start' style={{ gap: 10, padding: isMobile ? '0 10px' : 0, marginBottom: 10 }}>
        <Button
          fullWidth={isMobile}
          type='button'
          color='success'
          startIcon={
            <FontAwesomeIcon
              icon={faPlus}
              color="#FFFFFF"
              size="xs"
            />
          }
          onClick={() => history.push('/nova-integracao-de-api')}
        >
          Gerar novo token
        </Button>
        <Button
          fullWidth={isMobile}
          type='button'
          color='success'
          startIcon={
            <FontAwesomeIcon
              icon={faCopy}
              color="#FFFFFF"
              size="xs"
            />
          }
          onClick={() => window.open(process.env.REACT_APP_PUBLIC_API_DOCS_URL, '_blank')}
        >
          Acessar documentação
        </Button>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {isMobile && (
            <TableMobile
              loading={isLoading}
              data={(list || []).map(item => ({
                ...item,
                name: {
                  index: 1,
                  label: 'Nome',
                  value: (
                    <Typography color='textSecondary' variant='subtitle2' style={{ wordBreak: 'break-all' }}>
                      {item?.name}
                    </Typography>
                  )
                },
                token: {
                  index: 2,
                  label: 'Token',
                  value: (
                    <Typography color='textSecondary' variant='subtitle2' style={{ wordBreak: 'break-all' }}>
                      {`...${item?.token}`}
                    </Typography>
                  )
                },
                lastUsed: {
                  index: 3,
                  label: 'Último uso',
                  value: (
                    <Typography color='textSecondary' variant='subtitle2'>
                      {!!item?.lastUsed ? moment(item?.lastUsed, 'YYYY-MM-DD HH:mm:ss').format('DD/MM/YYYY') : '--'}
                    </Typography>
                  )
                },
                createdAt: {
                  index: 4,
                  label: 'Data de criação',
                  value: (
                    <Typography color='textSecondary' variant='subtitle2'>
                      {moment(item?.createdAt).utc(true).format('DD/MM/YYYY')}
                    </Typography>
                  )
                },
                expirationDate: {
                  index: 5,
                  label: 'Data de expiração',
                  value: (
                    <Typography color='textSecondary' variant='subtitle2'>
                      {getExpirationDate(item?.expiresAt, item?.createdAt)}
                    </Typography>
                  )
                }
              }))}
              actions={[
                {
                  action: rowData => (
                    <Tooltip placement='top' title='Editar token' enterTouchDelay={0}>
                      <IconButton onClick={() => {
                        return history.push({
                          pathname: '/editar-integracao-de-api',
                          state: {
                            data: {
                              userIntegrationId: rowData?.userId,
                              expirationDate: rowData?.expiresAt
                            }
                          }
                        });
                      }}>
                        <FontAwesomeIcon
                          icon={faPen}
                          color="#022A5C"
                          size="xs"
                        />
                      </IconButton>
                    </Tooltip>
                  )
                }
              ]}
            />
          )}
          {!isMobile && (
            <MTable
              loading={isLoading}
              headers={headers}
              data={(list || []).map(item => ({
                ...item,
                name: item?.name,
                token: `...${item?.token}`,
                lastUsed: !!item?.lastUsed ? moment(item?.lastUsed, 'YYYY-MM-DD HH:mm:ss').format('DD/MM/YYYY') : '--',
                createdAt: moment(item?.createdAt).utc(true).format('DD/MM/YYYY'),
                expiresAt: getExpirationDate(item?.expiresAt, item?.createdAt),
                expirationDate: item?.expiresAt
              }))}
              actions={[
                rowData => ({
                  icon: () => (
                    <FontAwesomeIcon
                      icon={faPen}
                      color="#022A5C"
                      size="xs"
                    />
                  ),
                  tooltip: 'Editar token',
                  onClick: (_, rowData) => {
                    return history.push({
                      pathname: '/editar-integracao-de-api',
                      state: {
                        data: {
                          userIntegrationId: rowData?.userId,
                          expirationDate: rowData?.expirationDate
                        }
                      }
                    });
                  }
                })
              ]}
            />
          )}
        </Grid>
      </Grid>
    </Loader>
  );
}